export interface WorkWeekValue {
  weeks: string[][];
  nonWorkingWeeks?: boolean[];
}

export const DAY_BTN_ACTIVE =
  'tw-bg-unm-interactive01-400 tw-text-white tw-border-unm-interactive01-400';
export const DAY_BTN_INACTIVE =
  'tw-bg-white tw-text-unm-adhoc2 tw-border-[#929292]';

export const DAY_SHORT = [
  'Sun',
  'Mon',
  'Tues',
  'Wed',
  'Thurs',
  'Fri',
  'Sat',
] as const;

export const DAY_FULL = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export const MAX_ERROR_TEXT = 'Max values: 24 hrs, 59 mins.';

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n));

const padTwoDigits = (n: number) => String(n).padStart(2, '0');

export const toTimeStr = (h: string, m: string): string =>
  `${padTwoDigits(clamp(Number.parseInt(h) || 0, 0, 24))}:${padTwoDigits(
    clamp(Number.parseInt(m) || 0, 0, 59),
  )}`;

export const parseTime = (t: string): [string, string] => {
  if (!t || t === '00:00') return ['', ''];
  const [h, m] = t.split(':');
  const hv = Number.parseInt(h) || 0;
  const mv = Number.parseInt(m) || 0;
  return [hv > 0 ? String(hv) : '', mv > 0 ? String(mv) : ''];
};

export const isOver = (v: string, max: number) =>
  v !== '' && !Number.isNaN(Number.parseInt(v)) && Number.parseInt(v) > max;

export const parseClamped = (value: string, max: number) =>
  clamp(Number.parseInt(value) || 0, 0, max);

const empty7 = (): string[] => new Array(7).fill('');

export function initFromValue(value?: { weeks?: string[][] }) {
  const week = value?.weeks?.[0];
  if (!week) {
    return {
      selectedDays: new Array<boolean>(7).fill(false),
      perDayHrs: empty7(),
      perDayMins: empty7(),
    };
  }
  return {
    selectedDays: week.map((t) => t !== '00:00'),
    perDayHrs: week.map((t) => parseTime(t)[0]),
    perDayMins: week.map((t) => parseTime(t)[1]),
  };
}

const INPUT_BASE =
  'tw-w-[70px] tw-h-8 tw-text-center tw-text-base tw-font-semibold ' +
  'tw-bg-unm-bg-light tw-border tw-rounded-lg ' +
  'tw-outline-none';
const INPUT_ENABLED =
  'tw-border-unm-border1 focus:tw-border-unm-interactive01-400 tw-cursor-text';
const INPUT_DISABLED =
  'tw-border-unm-border1 tw-opacity-40 tw-cursor-not-allowed';
const INPUT_ERROR =
  'tw-border-2 tw-border-[#B42318] focus:tw-border-[#B42318] tw-cursor-text';

export const inputClass = (disabled: boolean, error = false) =>
  `${INPUT_BASE} ${disabled ? INPUT_DISABLED : INPUT_ENABLED} ${
    error ? INPUT_ERROR : ''
  }`;

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const ordinal = (n: number): string => {
  if (n >= 11 && n <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const formatDateOrdinal = (d: Date): string =>
  `${ordinal(d.getDate())} ${MONTHS_LONG[d.getMonth()]}`;

export const getWeekDateRange = (weekIndex: number): string => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - dayOfWeek + weekIndex * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return `${formatDateOrdinal(weekStart)} - ${formatDateOrdinal(weekEnd)}`;
};

export const getDayPills = (weekData: string[]): string[] =>
  weekData
    .map((t, i) => {
      if (t === '00:00') return null;
      const [h, m] = t.split(':');
      const hv = Number.parseInt(h) || 0;
      const mv = Number.parseInt(m) || 0;
      const time = [hv > 0 && `${hv}h`, mv > 0 && `${mv}m`]
        .filter(Boolean)
        .join(' ');
      return `${DAY_SHORT[i]}: ${time}`;
    })
    .filter((s): s is string => s !== null);
